import SearchDropdown from '@allesauto/core/searchDropdown';
import { type Option } from '@allesauto/core/searchDropdown';
import SecondaryButton from '@allesauto/core/secondaryButton';
import { SchemaTypes } from '@allesauto/graphql';
import translate from '@allesauto/i18n';
import {
  useGoogleMapsGeocode,
  useGoogleMapsPlaceAutocomplete,
} from '@allesauto/utils/apiHooks';
import { ToastifyType } from '@allesauto/utils/consts';
import debounce from 'lodash/debounce';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

export interface GeoSearchProps {
  onSearchClick?: (
    latLng: SchemaTypes.GeoCoordinates,
    inputValue: Option | null
  ) => void;
  onClear?: () => void;
  selectedSearchOption: Option | null;
  isSearchRequired?: boolean;
  placeholder: string;
}

export default function GeoSearch({
  onSearchClick = () => null,
  onClear = () => null,
  selectedSearchOption = null,
  isSearchRequired = false,
  placeholder,
}: GeoSearchProps) {
  const intl = useIntl();

  const trackingIdentifier = `aa-geosearch`;

  const countryBoundary = useSelector(
    (state: any) => state.app.googleMaps.countryBoundary
  );

  const hasButtonClicked = useRef<boolean>(false);

  const [inputValue, setInputValue] = useState('');
  const [dropdownValue, setDropdownValue] = useState<Option | null>(
    selectedSearchOption
  );

  const MIN_AUTOCOMPLETE_LENGTH = 3;

  const {
    results: places,
    loading: placesLoading,
    error: placesError,
  } = useGoogleMapsPlaceAutocomplete({
    input: inputValue,
    language: intl.locale || intl.defaultLocale,
    components: countryBoundary,
    minLength: MIN_AUTOCOMPLETE_LENGTH,
  });

  const {
    result: latLng,
    loading: geoLoading,
    error: geoError,
  } = useGoogleMapsGeocode({
    placeId: dropdownValue?.value as string,
  });

  useEffect(() => {
    if (placesError || geoError) {
      import('@allesauto/core/toastify').then((module) => {
        module.toastifyFactory(
          ToastifyType.ERROR,
          translate(intl, 'global.ERRORS.DEALER_FINDER.GEO_SEARCH.HEADER'),
          translate(intl, 'global.ERRORS.DEALER_FINDER.GEO_SEARCH.TEXT')
        );
      });
    }
  }, [intl, placesError, geoError]);

  useEffect(
    () => {
      if (!hasButtonClicked.current && latLng && selectedSearchOption) {
        onSearchClick(latLng, selectedSearchOption);
        hasButtonClicked.current = true;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedSearchOption, latLng]
  );

  const onInputChange = useCallback(
    debounce((v) => setInputValue(v), 100),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const className = {
    wrapper: 'flex flex-col md:flex-row',
    dropdown: 'flex-1 mb-15px md:mb-0 md:mr-20px min-w-0',
  };

  return (
    <div className={className.wrapper}>
      <div className={className.dropdown}>
        <SearchDropdown
          label={placeholder}
          onInputChange={onInputChange}
          options={places.map((p) => ({
            value: p.place_id,
            label: p.description,
          }))}
          value={dropdownValue}
          onChange={(v) => {
            setDropdownValue(v as Option);

            if (!v) {
              onClear();
            }
          }}
          hideDropdownIndicator
          hideNoOptions={
            inputValue.length < MIN_AUTOCOMPLETE_LENGTH || placesLoading
          }
          fieldRequired={isSearchRequired}
          id={trackingIdentifier}
        />
      </div>
      <div>
        <SecondaryButton
          fullWidth
          onClick={() =>
            !placesLoading && !geoLoading && latLng
              ? onSearchClick(latLng, dropdownValue)
              : null
          }
          isLoading={placesLoading || geoLoading}
          disabled={placesLoading || geoLoading}
          trackingId={`${trackingIdentifier}-button`}
        >
          {translate(intl, 'ui.DEALER_FINDER.SEARCH_BUTTON')}
        </SecondaryButton>
      </div>
    </div>
  );
}
